import { faBars, faChevronDown, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, ReactNode, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useQueryParams } from "utils/useQueryParams";
import Loading from "../Loading";
import SearchBar from "../table/SearchBar";
import { useTranslation } from "react-i18next";
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps {
  name: string;
  label?: string;
  options: SelectOption[];
  isMulti?: boolean;
  placeholder?: string | ReactNode;
  isRequired?: boolean;
  withSearch?: boolean;
  isLoading?: boolean;
  searchFullSize?: boolean;
}

const SortableItem: FC<{ option: SelectOption; onRemove: (option: SelectOption) => void }> = ({ option, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.value,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="parent-animation-on-hover cursor-default bg-mainSection px-2 py-1 rounded-md flex items-center"
    >
      <span {...listeners} className="cursor-grab mr-1 rubberBand-on-hover">
        <FontAwesomeIcon icon={faBars} />
      </span>
      {option.label}
      <span
        onClick={(e) => {
          e.stopPropagation();
          onRemove(option);
        }}
        className="ml-2 cursor-pointer text-danger"
      >
        ×
      </span>
    </div>
  );
};

const SelectInputWithDrag: FC<SelectProps> = ({
  name,
  label,
  options,
  isMulti = false,
  isRequired,
  placeholder,
  withSearch,
  isLoading,
  searchFullSize,
}) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { getParam, setParams } = useQueryParams();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // 🔹 watch the field value from react-hook-form
  const fieldValue = watch(name);

  // 🔹 sync local state with RHF value
  useEffect(() => {
    if (fieldValue && JSON.stringify(fieldValue) !== JSON.stringify(selectedOptions)) {
      setSelectedOptions(fieldValue);
    }
  }, [fieldValue]);

  const handleSelect = (option: SelectOption, field: any) => {
    let updatedOptions: SelectOption[];

    if (isMulti) {
      const alreadySelected = selectedOptions.some((selected) => selected.value === option.value);
      updatedOptions = alreadySelected
        ? selectedOptions.filter((selected) => selected.value !== option.value)
        : [...selectedOptions, option];
    } else {
      updatedOptions = [option];
    }

    setSelectedOptions(updatedOptions);
    field.onChange(updatedOptions);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (field: any) => {
    setSelectedOptions([]);
    field.onChange([]);
  };

  const handleDragEnd = (event: any, field: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = selectedOptions.findIndex((item) => item.value === active.id);
      const newIndex = selectedOptions.findIndex((item) => item.value === over.id);
      const updatedOptions = arrayMove(selectedOptions, oldIndex, newIndex);

      setSelectedOptions(updatedOptions);
      field.onChange(updatedOptions);
    }
  };

  const handleSearchChange = (term: string) => {
    setParams({ searchText: term });
    setSearchTerm(term);
  };

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="flex flex-col gap-2 mb-3">
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-text mb-0">
          {t(label)}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="relative">
            {/* Selected / Placeholder */}
            <div
              onClick={() => (isLoading ? () => {} : setIsOpen(!isOpen))}
              className="parent-animation-on-hover bg-inputBg w-full px-3 py-2 border border-inputBg rounded-md shadow-sm cursor-pointer flex justify-between items-center"
            >
              {selectedOptions.length > 0
                ? selectedOptions.map((option) => option.label).join(", ")
                : <span className="text-gray-400">{placeholder || t("selectPlaceholder")}</span>}
              <FontAwesomeIcon icon={isOpen ? faChevronLeft : faChevronDown} className="w-3" />
            </div>

            {/* Dropdown */}
            {isOpen && (
              <div className="absolute z-10 mt-1 w-full bg-inputBg border border-inputBg rounded-md shadow-lg max-h-40 overflow-auto">
                {withSearch && (
                  <SearchBar
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    isFullSize={searchFullSize}
                    color="sidebar"
                  />
                )}
                {isLoading && (
                  <div className="px-2 py-3">
                    <Loading size="w-4 h-4" />
                  </div>
                )}
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option, field)}
                    className={`cursor-pointer px-3 py-2 hover:bg-hoverPrimary transition300 ${
                      selectedOptions.some((s) => s.value === option.value) ? "bg-primary text-text" : "text-text"
                    }`}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}

            {/* Multi Select + Drag & Drop */}
            {isMulti && selectedOptions.length > 0 && (
              <div className="mt-2 flex justify-between items-center">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => handleDragEnd(event, field)}>
                  <SortableContext items={selectedOptions.map((o) => o.value)} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-wrap gap-2">
                      {selectedOptions.map((option) => (
                        <SortableItem key={option.value} option={option} onRemove={(opt) => handleSelect(opt, field)} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <button onClick={() => handleClear(field)} className="bg-mainSection text-white px-2 py-1 rounded-md mt-2" type="button">
                  {t("clear")}
                </button>
              </div>
            )}
          </div>
        )}
      />

      {errors[name] && <p className="text-sm text-danger -mt-1">{String(errors[name]?.message)}</p>}
    </div>
  );
};

export default SelectInputWithDrag;
